import { LitElement, css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { shared } from '../styles/shared.js';
import { LocaleController } from '../i18n/locale-controller.js';
import { t } from '../i18n/t.js';
import { navigate, type Route } from '../router.js';
import { getAudioService } from '../audio/context.js';
import { MODE_ICONS, iconUrl, type IconAsset } from '../assets/icons.js';
import './digit-tile.js';

interface ModeTile {
  route: Route;
  labelKey: string;
  audioKey: string;
  accent: string;
  icon: IconAsset;
}

const TILES: ModeTile[] = [
  {
    route: 'recognize',
    labelKey: 'home.tile.recognize',
    audioKey: 'mode.recognize',
    accent: 'var(--color-accent-yellow)',
    icon: MODE_ICONS.recognize,
  },
  {
    route: 'count',
    labelKey: 'home.tile.count',
    audioKey: 'mode.count',
    accent: 'var(--color-accent-pink)',
    icon: MODE_ICONS.count,
  },
  {
    route: 'add',
    labelKey: 'home.tile.add',
    audioKey: 'mode.add',
    accent: 'var(--color-accent-cyan)',
    icon: MODE_ICONS.add,
  },
  {
    route: 'sub',
    labelKey: 'home.tile.sub',
    audioKey: 'mode.sub',
    accent: 'var(--color-accent-lime)',
    icon: MODE_ICONS.sub,
  },
];

@customElement('home-screen')
export class HomeScreen extends LitElement {
  static override styles = [
    shared,
    css`
      :host {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--space-6);
        height: 100%;
        padding: var(--space-5);
      }

      h1 {
        text-align: center;
      }

      .grid {
        display: grid;
        grid-template-columns: repeat(2, auto);
        gap: var(--space-5);
      }

      .mode-icon {
        width: 96px;
        height: 96px;
      }

      /* Compact landscape (phones): smaller title, tighter grid, smaller
         icons. Tile sizes themselves come from .tile--mode. */
      @media (max-height: 500px) {
        :host {
          gap: var(--space-3);
          padding: var(--space-3) var(--space-4);
        }

        h1 {
          font-size: var(--type-m);
          margin: 0;
        }

        .grid {
          grid-template-columns: repeat(4, auto);
          gap: var(--space-3);
        }

        .mode-icon {
          width: 48px;
          height: 48px;
        }
      }

      @media (max-height: 500px) and (max-width: 700px) {
        :host {
          gap: var(--space-2);
          padding: var(--space-2);
        }

        h1 {
          font-size: var(--type-s);
        }

        .grid {
          gap: var(--space-2);
        }

        .mode-icon {
          width: 32px;
          height: 32px;
        }
      }
    `,
  ];

  private locale = new LocaleController(this);

  private onTileTap(tile: ModeTile): void {
    try {
      void getAudioService().play(tile.audioKey, 'prompt');
    } catch {
      // Audio service may not be initialised in some test contexts.
    }
    navigate(tile.route);
  }

  override render() {
    return html`
      <h1 class="text-display">${t(this.locale.locale, 'home.title')}</h1>
      <div class="grid">
        ${TILES.map(
          (tile) => html`
            <digit-tile
              variant="mode"
              data-mode=${tile.route}
              style=${`--color-accent-current: ${tile.accent};`}
              .label=${t(this.locale.locale, tile.labelKey)}
              @tile-tap=${() => this.onTileTap(tile)}
            >
              <img class="mode-icon" src=${iconUrl(tile.icon)} alt="" aria-hidden="true" />
              <span>${t(this.locale.locale, tile.labelKey)}</span>
            </digit-tile>
          `,
        )}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'home-screen': HomeScreen;
  }
}
